import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography, CircularProgress } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider';
import { Button } from '../../../components/ui';
import AnalyticsPageHeader from '../components/AnalyticsPageHeader';
import { ROUTES } from '../../../constants/routes';
import { userInformationAtom } from '../../../Services/_states';
import useAnalyticsSx from '../hooks/useAnalyticsSx';
import useAnalyticsCatalog from '../hooks/useAnalyticsCatalog';
import useAnalyticsScope from '../hooks/useAnalyticsScope';
import useAnalyticsSuperAdmin from '../hooks/useAnalyticsSuperAdmin';
import useReportPreview from '../hooks/useReportPreview';
import useReportDimensions from '../hooks/useReportDimensions';
import useSavedReports from '../hooks/useSavedReports';
import useDashboardPins from '../hooks/useDashboardPins';
import useAnalyticsBuilderLayout from '../hooks/useAnalyticsBuilderLayout';
import useAnalyticsPermissions from '../hooks/useAnalyticsPermissions';
import { analyticsService } from '../services/analyticsService';
import { querySpecToBuilderState } from '../adapters/querySpecToBuilderState';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import StepDataSource from '../components/builder/StepDataSource';
import StepConfigureAndPreview from '../components/builder/StepConfigureAndPreview';
import PinToDashboardDialog from '../components/builder/PinToDashboardDialog';
import OrganisationPicker from '../components/home/OrganisationPicker';
import { buildQuerySpec } from '../adapters/buildQuerySpec';
import { resolveScopeFormIds } from '../adapters/chartDimensionAdapter';
import { ANALYTICS_COPY } from '../constants/analyticsCopy';
import { DEFAULT_CHART_TYPE } from '../constants/chartTypes';
import { getChartConfig } from '../utils/chartTypeMapping';
import { getOrganisationId } from '../../intervene/utils/organisationSelectUtils';
import { getAnalyticsOrganisationId } from '../utils/analyticsOrgScope';
import { DATASET_INTERVENE, usesIndependentScope } from '../constants/datasetScope';

function parseTableContext(searchParams) {
  const dataset = searchParams.get('dataset') || '';
  const accountId = searchParams.get('account_id') || '';
  const campaignId = searchParams.get('campaign_id') || '';
  const leadsFormId = searchParams.get('leads_form_id') || '';
  const formName = searchParams.get('form_name') || '';

  if (dataset !== 'records' || !accountId) {
    return null;
  }

  return {
    dataset: 'records',
    accountId,
    campaignIds: campaignId ? [campaignId] : [],
    leadsFormIds: leadsFormId ? [leadsFormId] : [],
    formName,
    scopeLocked: Boolean(campaignId && leadsFormId),
  };
}

function ReportBuilderContent() {
  const sx = useAnalyticsSx();
  useAnalyticsBuilderLayout();
  const navigate = useNavigate();
  const { reportId: editReportId } = useParams();
  const [searchParams] = useSearchParams();
  const tableContext = useMemo(() => parseTableContext(searchParams), [searchParams]);
  const tableContextAppliedRef = useRef(false);
  const presetDatasetAppliedRef = useRef(false);
  const editHydratedRef = useRef(false);

  const isSuperAdmin = useAnalyticsSuperAdmin();
  const permissions = useAnalyticsPermissions();
  const { showSnackBar } = useSnackBar();
  const userInfo = useRecoilValue(userInformationAtom);
  const [organisation, setOrganisation] = useState(null);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [savedReportId, setSavedReportId] = useState(null);
  const [isLoadingReport, setIsLoadingReport] = useState(Boolean(editReportId));
  const isEditMode = Boolean(editReportId);

  const [builderState, setBuilderState] = useState({
    dataset: '',
    accountId: '',
    campaignIds: [],
    leadsFormIds: [],
    chartType: DEFAULT_CHART_TYPE,
    metric: 'count',
    aggregation: 'count',
    measureField: '',
    dimension: '',
    tableColumns: [],
    filters: [],
    includedValues: [],
    dateRange: { preset: 'all_time' },
    templateId: '',
    botNumber: '',
    reportName: '',
    scopeLocked: false,
    scopeContext: {},
  });

  const orgId = useMemo(() => {
    if (isSuperAdmin) return getOrganisationId(organisation);
    return getAnalyticsOrganisationId(userInfo);
  }, [isSuperAdmin, organisation, userInfo]);

  const { catalog, isLoading: catalogLoading } = useAnalyticsCatalog();
  const { scope, isLoading: scopeLoading } = useAnalyticsScope(orgId);
  const { createReport, updateReport } = useSavedReports(orgId);
  const { pinReport } = useDashboardPins(orgId);

  const selectedDataset = catalog.datasets.find((d) => d.id === builderState.dataset);
  const {
    dimensions,
    groupByOptions,
    measureFieldOptions,
    timeFieldOptions,
    needsTableColumns,
    isLoadingFormFields,
  } = useReportDimensions(
    selectedDataset?.dimensions || [],
    {
      dataset: builderState.dataset,
      campaignIds: builderState.campaignIds,
      leadsFormIds: builderState.leadsFormIds,
      chartType: builderState.chartType,
    }
  );

  const queryState = useMemo(() => {
    const chartConfig = getChartConfig(builderState.chartType);
    const dimension = builderState.chartType === 'table'
      ? builderState.dimension
      : (builderState.dimension || (
        chartConfig.requiresDimension ? groupByOptions[0]?.id || '' : ''
      ));

    return {
      ...builderState,
      dimension,
      scopeFormIds: resolveScopeFormIds({
        dimension,
        groupByOptions,
        aggregation: builderState.aggregation,
        measureField: builderState.measureField,
        measureFieldOptions,
      }),
    };
  }, [
    builderState,
    groupByOptions,
    measureFieldOptions,
  ]);

  const { preview, previewError, isLoading: previewLoading } = useReportPreview(queryState, orgId);

  useEffect(() => {
    if (!editReportId || !orgId || catalogLoading || editHydratedRef.current) {
      return undefined;
    }
    let cancelled = false;
    async function loadReportForEdit() {
      setIsLoadingReport(true);
      const res = await analyticsService.getSavedReport(editReportId, orgId);
      if (cancelled) return;
      if (!res.ok) {
        showSnackBar(ANALYTICS_COPY.saveReportFailed, 'error');
        navigate(ROUTES.FEATURES.ANALYTICS);
        setIsLoadingReport(false);
        return;
      }
      setBuilderState(querySpecToBuilderState(res.data));
      editHydratedRef.current = true;
      setIsLoadingReport(false);
    }
    loadReportForEdit();
    return () => { cancelled = true; };
  }, [editReportId, orgId, catalogLoading, navigate, showSnackBar]);

  useEffect(() => {
    if (!tableContext || tableContextAppliedRef.current || catalogLoading || isEditMode) {
      return;
    }
    tableContextAppliedRef.current = true;
    const recordsDataset = catalog.datasets.find((d) => d.id === 'records');
    setBuilderState((prev) => ({
      ...prev,
      dataset: tableContext.dataset,
      accountId: tableContext.accountId,
      campaignIds: tableContext.campaignIds,
      leadsFormIds: tableContext.leadsFormIds,
      metric: recordsDataset?.metrics?.[0]?.id || 'count',
      chartType: DEFAULT_CHART_TYPE,
      scopeLocked: tableContext.scopeLocked,
      scopeContext: {
        formName: tableContext.formName,
      },
      reportName: tableContext.formName
        ? `${tableContext.formName} report`
        : prev.reportName,
    }));
  }, [tableContext, catalogLoading, catalog.datasets]);

  useEffect(() => {
    if (presetDatasetAppliedRef.current || tableContext || isEditMode || catalogLoading) {
      return;
    }
    const preset = searchParams.get('dataset');
    if (!preset) return;
    const match = catalog.datasets.find((item) => item.id === preset);
    if (!match) return;
    presetDatasetAppliedRef.current = true;
    const starterPreset = searchParams.get('preset');
    if (preset === 'intervene' && starterPreset === 'agent_benchmark') {
      setBuilderState((prev) => ({
        ...prev,
        dataset: 'intervene',
        chartType: 'bar',
        metric: 'avg_agent_response',
        dimension: 'response_agent_username',
        reportName: 'Agent Benchmarking (Response Time)',
      }));
      return;
    }
    setBuilderState((prev) => ({
      ...prev,
      dataset: match.id,
      metric: match.metrics?.[0]?.id || prev.metric,
    }));
  }, [searchParams, tableContext, isEditMode, catalogLoading, catalog.datasets]);

  useEffect(() => {
    if (builderState.aggregation === 'count') {
      return;
    }
    const available = measureFieldOptions.filter((item) => item.id !== builderState.dimension);
    const nextField = available.some((item) => item.id === builderState.measureField)
      ? builderState.measureField
      : (available[0]?.id || '');
    if (nextField === builderState.measureField) {
      return;
    }
    setBuilderState((prev) => ({ ...prev, measureField: nextField }));
  }, [
    builderState.aggregation,
    builderState.measureField,
    builderState.dimension,
    measureFieldOptions,
  ]);

  const patchBuilder = (patch) => {
    setBuilderState((prev) => ({ ...prev, ...patch }));
  };

  const canSave = () => {
    if (!builderState.dataset) return false;
    const independentScope = usesIndependentScope(builderState.dataset);
    const chartType = builderState.chartType;
    const config = getChartConfig(chartType);
    const hasDimension = builderState.dataset === 'whatsapp_templates'
      || !config.requiresDimension
      || Boolean(queryState.dimension);
    const hasTableScope = !needsTableColumns || independentScope || builderState.leadsFormIds.length > 0;
    const needsAccount = !independentScope;
    return Boolean(
      (!needsAccount || builderState.accountId)
      && builderState.reportName?.trim()
      && hasDimension
      && hasTableScope
    );
  };

  const handleSelectDataset = (dataset) => {
    if (dataset === builderState.dataset) return;
    patchBuilder({
      dataset,
      accountId: '',
      campaignIds: [],
      leadsFormIds: [],
      dimension: '',
      tableColumns: [],
      measureField: '',
      aggregation: 'count',
      filters: [],
      includedValues: [],
      chartType: DEFAULT_CHART_TYPE,
      templateId: '',
      dateRange: { preset: 'all_time' },
      reportName: '',
      metric: catalog.datasets.find((d) => d.id === dataset)?.metrics?.[0]?.id || '',
    });
  };

  const handleSave = async () => {
    if (!permissions.canCreate) {
      showSnackBar(ANALYTICS_COPY.createReportPermissionHint, 'warning');
      return;
    }
    const querySpec = buildQuerySpec(queryState);
    const payload = {
      name: builderState.reportName.trim(),
      query_spec: querySpec,
      visualization: { mode: builderState.chartType },
    };
    const res = isEditMode
      ? await updateReport(editReportId, payload)
      : await createReport(payload);
    if (res.ok) {
      const reportId = res.data?.report_id || editReportId;
      if (isEditMode) {
        showSnackBar(ANALYTICS_COPY.saveReportSuccess, 'success');
        navigate(ROUTES.FEATURES.ANALYTICS);
        return;
      }
      setSavedReportId(reportId);
      setPinDialogOpen(true);
      return;
    }
    const message = typeof res.data === 'string'
      ? res.data
      : res.data?.general_error || ANALYTICS_COPY.saveReportFailed;
    showSnackBar(message, 'error');
  };

  const handlePin = async () => {
    if (savedReportId) await pinReport(savedReportId);
    setPinDialogOpen(false);
    navigate(ROUTES.FEATURES.ANALYTICS);
  };

  const handleSkipPin = () => {
    setPinDialogOpen(false);
    if (savedReportId) {
      navigate(ROUTES.FEATURES.ANALYTICS);
    } else {
      navigate(ROUTES.FEATURES.ANALYTICS);
    }
  };

  return (
    <Box sx={sx.pageRoot}>
      <AnalyticsPageHeader
        title={isEditMode ? ANALYTICS_COPY.editReport : ANALYTICS_COPY.createReport}
        sectionLabel={isEditMode ? ANALYTICS_COPY.editReport : ANALYTICS_COPY.createReport}
        subtitle={isEditMode ? ANALYTICS_COPY.editReportSubtitle : ANALYTICS_COPY.createReportSubtitle}
        action={(
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="outline"
              onClick={() => navigate(ROUTES.FEATURES.ANALYTICS)}
            >
              Cancel
            </Button>
          </Stack>
        )}
      />
      {isSuperAdmin ? (
        <OrganisationPicker organisation={organisation} onChange={setOrganisation} sx={sx} />
      ) : null}

      {catalogLoading || isLoadingReport ? (
        <Stack alignItems="center" py={6}>
          <CircularProgress />
        </Stack>
      ) : (
        <Stack spacing={3}>
          {!tableContext && !isEditMode ? (
            <StepDataSource
              datasets={catalog.datasets}
              selectedId={builderState.dataset}
              onSelect={handleSelectDataset}
              sx={sx}
            />
          ) : null}

          {builderState.dataset ? (
            <>
              <StepConfigureAndPreview
                dataset={builderState.dataset}
                datasetLabel={selectedDataset?.label || ''}
                businessFunctions={scope.businessFunctions}
                accountId={builderState.accountId}
                campaignIds={builderState.campaignIds}
                leadsFormIds={builderState.leadsFormIds}
                scopeLocked={builderState.scopeLocked}
                scopeContext={builderState.scopeContext}
                onScopeChange={(patch) => {
                  if ('campaignIds' in patch || 'accountId' in patch || 'leadsFormIds' in patch) {
                    patchBuilder({
                      ...patch,
                      dimension: '',
                      tableColumns: [],
                      measureField: '',
                      aggregation: 'count',
                      filters: [],
                      includedValues: [],
                    });
                    return;
                  }
                  patchBuilder(patch);
                }}
                metrics={selectedDataset?.metrics || []}
                groupByOptions={groupByOptions}
                filterFieldOptions={dimensions}
                measureFieldOptions={measureFieldOptions}
                timeFieldOptions={timeFieldOptions}
                needsTableColumns={needsTableColumns}
                isLoadingFormFields={isLoadingFormFields}
                builderState={builderState}
                onChange={patchBuilder}
                preview={preview}
                previewError={previewError}
                isPreviewLoading={previewLoading}
                scopeLoading={scopeLoading}
                needsOrganisation={isSuperAdmin && !orgId}
                organisationId={orgId}
                querySpec={buildQuerySpec(queryState)}
                canExport={permissions.canExport}
                onExport={async (spec) => {
                  try {
                    await analyticsService.downloadExport(spec, orgId);
                  } catch {
                    showSnackBar(ANALYTICS_COPY.exportFailed, 'error');
                  }
                }}
                onSave={handleSave}
                canSave={canSave() && permissions.canCreate}
                isEditMode={isEditMode}
                sx={sx}
              />
            </>
          ) : null}
        </Stack>
      )}

      <PinToDashboardDialog
        open={pinDialogOpen}
        onPin={handlePin}
        onSkip={handleSkipPin}
      />
    </Box>
  );
}

export default function ReportBuilderPage() {
  return (
    <FeatureThemeProvider feature="analytics">
      <ReportBuilderContent />
    </FeatureThemeProvider>
  );
}
